import React, { useEffect, useState } from 'react'

import { useHistory, useLocation, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Paper } from '@material-ui/core'
import {
  EditOutlined as EditIcon,
  DeleteOutlineOutlined as DeleteIcon,
  ForumOutlined
} from '@material-ui/icons'
import sessionSlice, { fetchSessions, deleteSessionById } from 'store/session'
import { renderCol } from 'utils/renderColumns'
import { resetEventStore, fetchEventById } from 'store/event'

import ActionBar from 'components/ActionBar'
import ViewHeader from 'components/ViewHeader'
import getTableOptions from 'utils/getTableOptions'
import ResourceNotFound from 'components/ResourceNotFound'
import BasicTable from 'components/BasicTable'
import Info from 'components/Info'
import { convertTimestampToLocalString } from 'utils/times'
import Wrapper from 'components/Wrapper'
import ConfirmModal from 'components/ConfirmationModal'
import { toast } from 'react-toastify'

const SessionListing = () => {
  const dispatch = useDispatch()
  const { eventId } = useParams()
  const history = useHistory()
  const location = useLocation()
  const { user } = useSelector((state) => state.auth)
  const { sessions, inProgress: sessionInProgress } = useSelector((state) => state.session)
  const { event, inProgress: eventInProgress } = useSelector((state) => state.event)

  const [deletedSessionId, setDeletedSessionId] = useState('')
  const [loading, setLoading] = useState(false)

  const { conference } = useSelector((state) => state.conference)

  const closeModal = () => {
    setDeletedSessionId('')
  }

  const handleDeleteSession = async () => {
    if (deletedSessionId) {
      setLoading(true)
      await dispatch(deleteSessionById(deletedSessionId))
      toast.success('Session deleted successfully.')
    }
    setLoading(false)
  }

  const inProgress = sessionInProgress || eventInProgress

  useEffect(() => {
    if (eventId && user?.conferenceId) {
      const unsub = fetchSessions(eventId, user?.conferenceId, (data) => {
        dispatch(sessionSlice.actions.setSessions(data))
      })
      dispatch(fetchEventById(eventId))
      return () => {
        unsub && unsub()
        dispatch(sessionSlice.actions.setSessions([]))
      }
    }
  }, [eventId, user?.conferenceId])

  const tableHead = [
    {
      name: 'Sr #',
      options: {
        customBodyRender: (value) => <div style={{ minWidth: '90px' }}>{value}</div>
      }
    },
    'Title',
    {
      name: 'Start Time',
      options: {
        customBodyRender: (value) => (
          <div style={{ minWidth: '130px' }}>
            {convertTimestampToLocalString(value, conference?.timezone, conference?.is24hrFormat)}
          </div>
        )
      }
    },
    {
      name: 'End Time',
      options: {
        customBodyRender: (value) => (
          <div style={{ minWidth: '130px' }}>
            {convertTimestampToLocalString(value, conference?.timezone, conference?.is24hrFormat)}
          </div>
        )
      }
    },
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]

  const goBack = () => {
    dispatch(resetEventStore())
    history.goBack()
  }

  const formatTableData = (sessions) =>
    sessions.map((session) => [
      session?.srNo,
      session.title,
      session.startDate,
      session.endDate,
      <div style={{ width: 130 }}>
        <ForumOutlined
          color="action"
          style={{ marginRight: 10, cursor: 'pointer' }}
          onClick={() => history.push(`${location.pathname}/${session.id}/talks`)}
        />
        <EditIcon
          color="action"
          style={{ marginRight: 10, cursor: 'pointer' }}
          onClick={() => {
            history.push(`${location.pathname}/edit/${session.id}`)
          }}
        />
        <DeleteIcon
          color="action"
          style={{ marginRight: 10, cursor: 'pointer' }}
          onClick={() => setDeletedSessionId(session?.id)}
        />
      </div>
    ])

  const options = getTableOptions()

  return (
    <>
      <ViewHeader title="Event Sessions">
        <div style={{ marginRight: 10 }}>
          <ActionBar actionText="Back" onAction={() => goBack()} />
        </div>
        <ActionBar
          actionText="New Session"
          onAction={() => history.push(`${location.pathname}/new`)}
          searchPlaceholder="Search Events"
        />
      </ViewHeader>

      <Wrapper inProgress={inProgress}>
        {event && event.id === eventId && (
          <Info
            eventTitle={event.title}
            eventStartDate={event ? event.startDate : null}
            eventEndDate={event ? event.endDate : null}
            pinCode={conference?.havePolls && event?.pinCode}
            timezone={conference?.timezone}
            is24hrFormat={conference?.is24hrFormat}
          />
        )}
        <Paper variant="outlined" style={{ marginTop: '20px' }}>
          {sessions && sessions.length ? (
            <BasicTable data={formatTableData(sessions)} columns={tableHead} options={options} />
          ) : (
            <ResourceNotFound message="No session available" />
          )}
        </Paper>
      </Wrapper>
      <ConfirmModal
        dialogTitle="Delete session"
        dialogText="Are you sure you want to delete this session?"
        open={Boolean(deletedSessionId)}
        closeModal={closeModal}
        handleConfirm={handleDeleteSession}
        inProgress={loading}
      />
    </>
  )
}

export default SessionListing
